import { ValidationError } from "sequelize"
import {
    RrsCategory,
    RrsCategoryCreate,
    RrsCategoryImport,
    RrsCategorySave,
    RrsCategoryChangeStatus,
    RrsCategoryDelete,
} from "server/models"

import { ASC, PUBLISHED, UNPUBLISHED } from "const"
import { PublishedUnpublished } from "client/utils"
import { GET, POST, validate, sort } from "client/store/utils"
import {
    LangType,
    RrsCategoryList,
    RrsCategoryChildParent,
    RrsCategoryValidation,
    RrsCategoryFilters,
    PrepareRrsCategoryNestedIds,
} from "client/store"

export const LoadAllRrsCategories = (accountId: number, projectId: number) => (
    GET(`/rrsCategory/accountId/${accountId}/projectId/${projectId}`)
    .then((categories: RrsCategory[]) => {
        const categoryList: RrsCategoryList = {}
        const childParent: RrsCategoryChildParent = {}

        categories.forEach((category) => {
            categoryList[category.id] = {
                ...category,
                customVariablesParsed: category.customVariables ? JSON.parse(category.customVariables) : null,
            }
            childParent[category.id] = category.categoryId
        })
        return { categoryList, childParent }
    })
)

export const LoadRrsCategoryById = (categoryId: number, accountId: number, projectId: number) => (
    GET(`/rrsCategory/id/${categoryId}/accountId/${accountId}/projectId/${projectId}`)
    .then((category: RrsCategory) => category)
)

export const SaveRrsCategory = (
    id: number,
    category: RrsCategoryCreate,
    lang: LangType,
) => {
    const data: RrsCategorySave = {
        id,
        category,
    }
    return POST(
        "/rrsCategory/save",
        JSON.stringify(data),
    ).then((res) => {
        const newCategory = res as RrsCategory
        const error = res as ValidationError

        return {
            newCategoryId: newCategory.id || null,
            validation: <RrsCategoryValidation>validate(error, lang),
        }
    })
}

export const ChangeParentsRrsCategory = (
    oldCategoryId: number,
    newCategoryId: number,
    accountId: number,
    projectId: number,
) => (
    // eslint-disable-next-line max-len
    GET(`/rrsCategory/changeParents/oldCategoryId/${oldCategoryId}/newCategoryId/${newCategoryId}/accountId/${accountId}/projectId/${projectId}`)
)

export const ChangeRrsCategoryStatus = (category: RrsCategory) => {
    const status: PublishedUnpublished = category.status === PUBLISHED ? UNPUBLISHED : PUBLISHED
    const data: RrsCategoryChangeStatus = {
        id: category.id,
        status,
    }
    return POST(
        "/rrsCategory/changeStatus",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
            ? status
            : category.status
    ))
}

export const DeleteRrsCategories = (data: RrsCategoryDelete) => (
    POST(
        "/rrsCategory/delete",
        JSON.stringify(data),
    )
)

export const ImportRrsCategories = (data: RrsCategoryImport) => (
    POST(
        "/rrsCategory/import",
        JSON.stringify(data),
    )
)

export const CopyDefaultRrsCategories = (
    accountId: number,
    projectId: number,
    defaultCategoriesAssigned: boolean,
) => (
    LoadAllRrsCategories(defaultCategoriesAssigned ? accountId : 1, null).then((res) => {
        const groupedIds = PrepareRrsCategoryNestedIds(Object.values(res.categoryList))
        let nestedIds = groupedIds.length ? groupedIds[0].nestedIds : []

        // import only published on top level
        nestedIds = nestedIds.filter((row) => {
            const category = res.categoryList[Number(row.id)]
            return category.status === PUBLISHED
        })

        const importData: RrsCategoryImport = {
            nested: nestedIds,
            categoryList: res.categoryList,
            accountId,
            projectId,
        }
        return ImportRrsCategories(importData)
    })
)

export const SortRrsCategories = (
    categories: RrsCategory[],
    filters: RrsCategoryFilters,
) => {
    const categoriesFiltered = categories.filter((category) => {
        let showRow = true
        if (
            filters.search
            && category.name.toUpperCase().indexOf(filters.search.trim().toUpperCase()) < 0
            && category.shortName.toUpperCase().indexOf(filters.search.trim().toUpperCase()) < 0
        ) showRow = false
        return showRow
    })

    const categoriesByOrder: RrsCategory[] = []
    const categoriesByName: RrsCategory[] = []
    categoriesFiltered.forEach((category) => {
        if (category.order !== null) categoriesByOrder.push(category)
        else categoriesByName.push(category)
    })

    categoriesByOrder.sort((a, b) => sort(a, b, "order", ASC))
    categoriesByName.sort((a, b) => sort(a, b, "name", ASC))

    return categoriesByOrder.concat(categoriesByName)
}
